import {
  addressHelpers,
  statesByCountry,
  type AddressSuggestion,
  type CountryCode,
  type GooglePlacePrediction,
  type GooglePlacesAutocompleteResponse,
} from "@wayflyer/flyui";

import type { ParsedAddress, PlaceDetails } from "./google-places";

// Google Places sometimes returns full state names ("Iowa") rather than the abbreviation
// ("IA"), and the new Places API doesn't accept a state-level restriction. Expand each
// configured state to its abbreviation + full name so the filter matches either form.
const expandStateAliases = (states: string[], countries?: CountryCode[]): string[] => {
  const aliasSets = countries
    ?.map((code) => statesByCountry[code as keyof typeof statesByCountry])
    .filter((entries): entries is { id: string; text: string }[] => Array.isArray(entries));
  if (!aliasSets?.length) {
    return states;
  }
  const aliases = new Set<string>();
  for (const state of states) {
    aliases.add(state);
    const upper = state.toUpperCase();
    for (const entries of aliasSets) {
      const match = entries.find((entry) => entry.id.toUpperCase() === upper || entry.text.toUpperCase() === upper);
      if (match) {
        aliases.add(match.id);
        aliases.add(match.text);
      }
    }
  }
  return [...aliases];
};

// Checks whether any of a prediction's text fields names a configured state as a whole
// word. All three are checked rather than just the secondary text: Google presents `text`
// and `structuredFormat` as independent representations, and its reference warns against
// deriving one from the other, so the locality is not guaranteed to be in `secondaryText`.
const matchesStateFilter = (prediction: GooglePlacePrediction | undefined, states: string[]): boolean => {
  const haystack = [
    prediction?.text?.text,
    prediction?.structuredFormat?.mainText?.text,
    prediction?.structuredFormat?.secondaryText?.text,
  ]
    .filter(Boolean)
    .join(" ")
    .toUpperCase();

  return states.some((state) => new RegExp(`\\b${state.toUpperCase().replace(/\s+/g, "\\s+")}\\b`).test(haystack));
};

export const createAddressFieldSetters = <T>(
  form: {
    setValue: (name: keyof T, value: string, options?: { shouldValidate?: boolean; shouldDirty?: boolean }) => void;
  },
  fieldMapping: {
    streetAddress?: keyof T;
    addressLine2?: keyof T;
    city?: keyof T;
    state?: keyof T;
    postalCode?: keyof T;
    country?: keyof T;
    formattedAddress?: keyof T;
  }
) => {
  const clearAddressFields = (): void => {
    Object.values(fieldMapping).forEach((formField) => {
      if (formField) {
        form.setValue(formField, "");
      }
    });
  };

  const setAddressFields = (address: ParsedAddress, _details: PlaceDetails, mainText?: string): void => {
    // Google omits street_number/route for places named rather than numbered (a
    // convention centre, say). The suggestion's main text is the closest thing to a
    // street line in that case.
    const fieldValues = addressHelpers.mapToFieldValues(
      { ...address, addressLine1: address.addressLine1 || mainText?.trim() },
      {
        addressLine1: fieldMapping.streetAddress as string | undefined,
        addressLine2: fieldMapping.addressLine2 as string | undefined,
        city: fieldMapping.city as string | undefined,
        state: fieldMapping.state as string | undefined,
        postalCode: fieldMapping.postalCode as string | undefined,
        countryCode: fieldMapping.country as string | undefined,
        formattedAddress: fieldMapping.formattedAddress as string | undefined,
      }
    );

    clearAddressFields();

    Object.entries(fieldValues).forEach(([formField, value]) => {
      if (value?.trim()) {
        form.setValue(formField as keyof T, value.trim(), { shouldValidate: true, shouldDirty: true });
      }
    });
  };

  return Object.assign(setAddressFields, { clear: clearAddressFields });
};

export const processAutocompleteOptions = (
  response: GooglePlacesAutocompleteResponse,
  states?: string[],
  countries?: CountryCode[]
): AddressSuggestion[] => {
  if (!states?.length) {
    return addressHelpers.google.toAddressSuggestions(response);
  }

  // Filter before mapping. A suggestion carries only the main and secondary text, so
  // filtering afterwards cannot see the full prediction text and drops addresses whose
  // state is named only there.
  const expandedStates = expandStateAliases(states, countries);
  return addressHelpers.google.toAddressSuggestions({
    suggestions: (response.suggestions ?? []).filter(({ placePrediction }) =>
      matchesStateFilter(placePrediction, expandedStates)
    ),
  });
};
